import {Component, ElementRef, OnDestroy, ViewChild} from '@angular/core';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {ICellEditorParams} from 'ag-grid-community';
import {Store} from '@ngrx/store';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

import {GridUtils} from '../../../../shared-utilities/utils-old/grid-utils-old/grid-utils';
import {NumberUtils} from '../../../../shared-utilities/utils-old/shared-utils-old/number-utils';
import {
  calculateGPFromPrice,
  calculatePriceFromGpValue,
} from '../../../../shared/shared-utils/calculations/calculations-stock';
import {convertToDecimal} from '../../../../shared/shared-utils/number/number.utils';
import {IStore} from '../../../../shared/shared-models/store/store';

import {
  updatePriceGpForNgpReport,
  updateSingleNGPReportVisibleField,
} from '../../../../features/ngp-report/store/ngp-report.actions';

import {
  setPriceGpForStockManager,
  updateStockManagerSingleVisibleField,
} from '../../../../features/stock-manager/store/stock-manager.actions';
import {stockManagerHasEditableFieldChanges} from '../../../../features/stock-manager/utils/stock-manager-update.utils';

import {selectNGPVisibleFields} from '../../../../features/ngp-report/store/ngp-report.selectors';
import {selectStockMangerVisibleFields} from '../../../../features/stock-manager/store/stock-manager.selectors';
import {selectCurrentNavigationPage, selectSelectedStore} from '../../../../features/user-access/user-stores-store/user-store.selectors';

import {NGPReport} from '../../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {StockItem} from '../../../../shared-utilities/models-old/datastructures';
import {NavigationPageEnum} from '../../../../features/user-access/models/navigations.enums';

@Component({
  selector: 'app-cell-editor-price-ngp',
  templateUrl: './cell-editor-price-nominal-g-p.component.html',
  styleUrls: ['./cell-editor-price-nominal-g-p.component.scss'],
  standalone: false,
})
export class CellEditorPriceNominalGPComponent implements ICellEditorAngularComp, OnDestroy {
  @ViewChild('input', {static: true}) input: ElementRef;

  private destroy$ = new Subject<boolean>();
  private colDefField: string;
  private currentPage: string;
  private currentValue: number;
  private original: number;
  private params: ICellEditorParams<NGPReport | StockItem, number>;
  private visibleFields: Record<string, boolean> = {};
  private currentSelectedStore: IStore | undefined;

  constructor(private readonly store: Store) {
  }

  //#region GETTERS

  get cellParams(): ICellEditorParams<NGPReport | StockItem, number> {
    return this.params;
  }

  get cellParamData(): NGPReport | StockItem {
    return this.cellParams.data;
  }

  get cellParamDataAtColDefField(): number {
    return Number(this.cellParamData[this.columnDefField]);
  }

  get cellParamOriginalDataValueAtColDefField(): number {
    return Number(this.cellParamData.originalValue?.[this.columnDefField]?.value);
  }

  get columnDefField(): string {
    return this.colDefField;
  }

  get isRevertIconVisible(): boolean {
    return this.cellParamOriginalDataValueAtColDefField === this.value && this.columnDefField !== 'nominalGP';
  }

  get currentNavigationPage(): string {
    return this.currentPage;
  }

  get isCurrentNavigationPageNGPReport(): boolean {
    return this.currentPage === NavigationPageEnum.NGPReport;
  }

  get isCurrentNavigationPageStockManager(): boolean {
    return this.currentPage === NavigationPageEnum.StockManager;
  }

  get originalValue(): number {
    return this.original;
  }

  get value(): number {
    return this.currentValue;
  }

  set cellParams(params: ICellEditorParams<NGPReport | StockItem, number>) {
    this.params = params;
  }

  set cellParamDataAtColDefField(data: number) {
    this.cellParamData[this.colDefField] = data;
  }

  set columnDefField(field: string) {
    this.colDefField = field;
  }

  set currentNavigationPage(page: string) {
    this.currentPage = page;
  }

  set originalValue(value: number) {
    this.original = value;
  }

  set value(value: number) {
    this.currentValue = value;
  }

  set currentVisibleFields(visibleFields: Record<string, boolean>) {
    this.visibleFields = visibleFields;
  }

  //#endregion

  //#region LIFECYCLE

  afterGuiAttached(): void {
    this.input?.nativeElement?.focus();
  }

  agInit(params: ICellEditorParams<NGPReport | StockItem, number>): void {
    this.cellParams = {...params, data: {...params.data}};
    this.columnDefField = this.cellParams.colDef.field;
    this.value = this.cellParamDataAtColDefField;
    this.originalValue = this.value;

    this.store.select(selectCurrentNavigationPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe((page: string) => {
        this.currentNavigationPage = page;

        const visibleFieldsSelector = this.isCurrentNavigationPageNGPReport
          ? selectNGPVisibleFields
          : selectStockMangerVisibleFields;

        this.store.select(visibleFieldsSelector)
          .pipe(takeUntil(this.destroy$))
          .subscribe((visibleFields) => {
            this.currentVisibleFields = visibleFields;
          });
      });

    this.store.select(selectSelectedStore)
      .pipe(takeUntil(this.destroy$))
      .subscribe((store: IStore) => {
        this.currentSelectedStore = store;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  //#endregion

  //#region PUBLIC METHODS

  getValue(): number {
    this.markAsEdited();
    this.updateVisibility();

    if (this.isInvalidValue()) {
      this.resetToOriginalValue();
    }

    if (this.hasCurrentFieldChanged()) {
      if (
        this.isCurrentNavigationPageNGPReport &&
        (this.colDefField === 'nominalGP' || this.colDefField === 'sellPriIncl1')
      ) {
        this.store.dispatch(updatePriceGpForNgpReport({
          ngpReport: this.cellParamData as NGPReport,
          field: this.colDefField,
        }));
      }

      if (this.isCurrentNavigationPageStockManager) {
        return this.commitStockManagerPriceGpChange();
      }
    }

    return this.value;
  }

  onEnterKeyDown(): void {
    this.onKeyUp();
  }

  onKeyUp(): void {
    this.cellParams.value = this.value;
    this.cellParams.data[this.colDefField] = this.value;
    if (this.isCurrentNavigationPageStockManager) {
      this.applyLinkedPriceGpToRowData();
      this.refreshStockManagerPriceGpRow();
      return;
    }
    GridUtils.updateIsEditedAndForceRefresh(this.cellParams);
  }

  onUndoChangesClick(): void {
    this.resetToOriginalValue();
    GridUtils.removeIsEditingFlagAndDispatch(
      this.cellParamData,
      this.columnDefField,
      this.store,
      this.currentNavigationPage,
    );
    GridUtils.updateIsEditedAndForceRefresh(this.cellParams);
    this.originalValue = undefined;
    this.getValue();
  }

  selectText(): void {
    setTimeout(() => {
      this.input.nativeElement.focus();
      this.input.nativeElement.select();
    }, 1);
  }

  //#endregion

  //#region PRIVATE METHODS

  private isInvalidValue(): boolean {
    return this.value == null ||
      isNaN(this.value) ||
      (!['nominalGP', 'sellPriIncl1'].includes(this.colDefField) && this.value === 0);
  }

  private resetToOriginalValue(): void {
    this.value = this.cellParamOriginalDataValueAtColDefField;
    this.cellParamDataAtColDefField = this.value;
  }

  private updateVisibility(): void {
    const colId = this.colDefField === 'sellPriIncl1' ? 'nominalGP' : 'sellPriIncl1';
    if (!this.visibleFields.nominalGP) {
      const action = this.isCurrentNavigationPageNGPReport
        ? updateSingleNGPReportVisibleField({colId, value: true})
        : updateStockManagerSingleVisibleField({colId, value: true});

      this.store.dispatch(action);
    }
  }

  private hasCurrentFieldChanged(): boolean {
    return this.value !== this.cellParamOriginalDataValueAtColDefField;
  }

  private commitStockManagerPriceGpChange(): number {
    const formattedValue = +NumberUtils.formatNumberToDecimals(this.value, 2);
    this.cellParamDataAtColDefField = formattedValue;
    this.applyLinkedPriceGpToRowData();
    this.store.dispatch(setPriceGpForStockManager({
      stockItem: this.cellParamData as StockItem,
      field: this.colDefField,
    }));
    this.refreshStockManagerPriceGpRow();
    return formattedValue;
  }

  private applyLinkedPriceGpToRowData(): void {
    if (!this.currentSelectedStore) {
      return;
    }

    const stockItem = this.cellParamData as StockItem;
    if (this.colDefField === 'sellPriIncl1') {
      const updatedItem = calculateGPFromPrice(stockItem, this.currentSelectedStore, 2);
      stockItem.nominalGP = updatedItem.nominalGP;
      return;
    }

    if (this.colDefField === 'nominalGP') {
      if (stockItem.nominalGP >= 100) {
        stockItem.nominalGP = 99.99;
        this.value = stockItem.nominalGP;
      }
      stockItem.sellPriIncl1 = convertToDecimal(
        calculatePriceFromGpValue(this.currentSelectedStore, stockItem),
        2,
      );
    }
  }

  private refreshStockManagerPriceGpRow(): void {
    GridUtils.updateIsEditedAndForceRefresh(this.cellParams);
    const rowNode = this.cellParams.node;
    if (!rowNode) {
      return;
    }
    this.cellParams.api.refreshCells({
      rowNodes: [rowNode],
      columns: ['sellPriIncl1', 'nominalGP', 'icons'],
      force: true,
    });
  }

  private markAsEdited(): void {
    if (this.isCurrentNavigationPageStockManager) {
      this.cellParamData.isEdited = stockManagerHasEditableFieldChanges(this.cellParamData as StockItem);
      return;
    }

    this.cellParamData.isEdited =
      this.cellParams.value !== this.cellParamOriginalDataValueAtColDefField;
  }

  //#endregion
}
